<!--
  SupportDesk.svelte — the agent queue.

  Views across the top answer "what should I pick up next?" — everything open,
  nobody's, mine, waiting on the team, breaching — each with its count from the
  desk stats, so the size of each pile is visible before it is opened. Filters
  narrow a view; the table sorts server-side (by rating too, unrated last); a
  selection brings up the bulk bar, which applies one status, priority or
  assignee to every ticket ticked.

  Every filter change is an explicit handler that reloads, rather than an effect
  watching the filter state: the list is a consequence of what the agent did,
  and an effect that both reads and writes the query state is exactly the shape
  that loops.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Badge,
		Button,
		Checkbox,
		Input,
		Select,
		Spinner,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import AbstractDropdown from '../../components/AbstractDropdown.svelte';
	import {
		EmptyState,
		PageHeader,
		Panel,
		RelativeTime,
		RoutedButton,
		RoutedLink,
		SortableTableHeadCell,
		SupportPriorityBadge,
		SupportSLATimer,
		SupportStars,
		SupportStatusBadge
	} from '../../components';
	import {
		bulkUpdateDeskTickets,
		getSupportDeskStats,
		getSupportOverview,
		listDeskTickets,
		listSupportAgents
	} from '../../AppNetwork';
	import type {
		SortDirection,
		SupportDeskQuery,
		SupportDeskStats,
		SupportDeskView,
		SupportPerson,
		SupportProduct,
		SupportTicketPriority,
		SupportTicketStatus,
		SupportTicketSummary
	} from '../../AppTypes';
	import {
		SUPPORT_OPEN_STATUSES,
		SUPPORT_PRIORITIES,
		SUPPORT_STATUSES,
		supportPriorityLabel,
		supportStatusLabel
	} from '../../support/supportFormat';
	import { toastError, toastSuccess } from '../../toast';
	import { t, t2 } from '../../i18n';

	/** Page size. */
	const limit = 25;

	/** The views across the top, with the stats field each one's count comes from. */
	const VIEWS: { key: SupportDeskView; label: string; icon: string; stat: keyof SupportDeskStats }[] = [
		{ key: 'all', label: 'All open', icon: 'fa-inbox', stat: 'open' },
		{ key: 'unassigned', label: 'Unassigned', icon: 'fa-user-slash', stat: 'unassigned' },
		{ key: 'mine', label: 'Mine', icon: 'fa-user', stat: 'mine' },
		{ key: 'awaiting_agent', label: 'Awaiting agent', icon: 'fa-hourglass-half', stat: 'awaitingAgent' },
		{ key: 'breaching', label: 'Breaching', icon: 'fa-triangle-exclamation', stat: 'breaching' }
	];

	/** The status filter's "every open status" choice, which is the default. */
	const OPEN = 'open';

	/** The page of tickets on screen. */
	let tickets: SupportTicketSummary[] = $state([]);
	/** How many match in all. */
	let total = $state(0);
	/** Whether the list is loading. */
	let loading = $state(true);
	/** Failure of the list. */
	let error = $state('');
	/** The headline counts, or `null` until they arrive. */
	let stats = $state<SupportDeskStats | null>(null);
	/** Every agent, for the assignee filter and bulk assignment. */
	let agents: SupportPerson[] = $state([]);
	/** Every support-enabled product, for the product filter. */
	let products: SupportProduct[] = $state([]);

	/** The view selected. */
	let view: SupportDeskView = $state('all');
	/** Status filter: `open`, empty for any, or one status. */
	let statusFilter = $state(OPEN);
	/** Priority filter; empty for any. */
	let priorityFilter = $state('');
	/** Product filter; empty for any. */
	let productFilter = $state('');
	/** Assignee filter; empty for anyone. */
	let assigneeFilter = $state('');
	/** Free-text search. */
	let search = $state('');
	/** Sort key. */
	let sortBy: NonNullable<SupportDeskQuery['sortBy']> = $state('lastActivityAt');
	/** Sort direction. */
	let sortDir: SortDirection = $state('desc');
	/** Current page, from zero. */
	let page = $state(0);

	/** Ticket keys ticked for a bulk change. */
	let selected: string[] = $state([]);
	/** Bulk status to apply; empty leaves status alone. */
	let bulkStatus = $state('');
	/** Bulk priority to apply; empty leaves priority alone. */
	let bulkPriority = $state('');
	/** Bulk assignee to apply: empty leaves it alone, `none` unassigns. */
	let bulkAssignee = $state('');
	/** Whether a bulk change is in flight. */
	let applyingBulk = $state(false);

	/** The statuses the status filter sends. */
	function statusQuery(): SupportTicketStatus[] | undefined {
		/* The default: every status a ticket can still be worked in. */
		if (statusFilter === OPEN) return SUPPORT_OPEN_STATUSES;
		return statusFilter ? [statusFilter as SupportTicketStatus] : undefined;
	}

	/** Loads the current page of the queue. */
	async function loadTickets(): Promise<void> {
		loading = true;
		error = '';
		/** Views, filters, sort and paging are all applied server-side. */
		try {
			const result = await listDeskTickets({
				view,
				status: statusQuery(),
				priority: priorityFilter ? [priorityFilter as SupportTicketPriority] : undefined,
				applicationUUID: productFilter || undefined,
				assigneeUUID: assigneeFilter || undefined,
				search: search.trim() || undefined,
				sortBy,
				sortDir,
				limit,
				offset: page * limit
			});
			tickets = result.items;
			total = result.total;
			/* A selection only means something for rows still on screen. */
			selected = selected.filter((key) => tickets.some((ticket) => ticket.ticketKey === key));
		} catch (e) {
			tickets = [];
			error = e instanceof Error ? e.message : t2('Failed to load tickets');
		} finally {
			loading = false;
		}
	}

	/** Refreshes the counts on the view tabs. Best-effort: the tabs simply show no count without them. */
	async function loadStats(): Promise<void> {
		/** Counts are a convenience, never a reason to fail the page. */
		try {
			stats = await getSupportDeskStats();
		} catch {
			stats = null;
		}
	}

	/** Loads the agents and products the filters offer. */
	async function loadFilterChoices(): Promise<void> {
		const [agentList, overview] = await Promise.allSettled([listSupportAgents(), getSupportOverview()]);
		agents = agentList.status === 'fulfilled' ? agentList.value : [];
		products = overview.status === 'fulfilled' ? overview.value.products : [];
	}

	/** Restarts the list from its first page — what every filter change does. */
	function reload(): void {
		page = 0;
		loadTickets();
	}

	/** Switches view. */
	function selectView(next: SupportDeskView): void {
		view = next;
		reload();
	}

	/** Whether any filter differs from the default, which is what offers "Clear". */
	let hasFilters = $derived(
		!!search.trim() || statusFilter !== OPEN || !!priorityFilter || !!productFilter || !!assigneeFilter
	);

	/** Resets every filter to its default and reloads. */
	function clearFilters(): void {
		search = '';
		statusFilter = OPEN;
		priorityFilter = '';
		productFilter = '';
		assigneeFilter = '';
		reload();
	}

	/** Re-sorts the list. */
	function handleSort(key: string, dir: SortDirection): void {
		sortBy = key as typeof sortBy;
		sortDir = dir;
		reload();
	}

	/** Ticks or unticks one ticket. */
	function toggle(ticketKey: string): void {
		selected = selected.includes(ticketKey) ? selected.filter((key) => key !== ticketKey) : [...selected, ticketKey];
	}

	/** Ticks every ticket on the page, or none if all already are. */
	function toggleAll(): void {
		selected = allSelected ? [] : tickets.map((ticket) => ticket.ticketKey);
	}

	/** Whether every row on the page is ticked. */
	let allSelected = $derived(tickets.length > 0 && selected.length === tickets.length);

	/** Whether the bulk bar has a change to apply. */
	let hasBulkChange = $derived(bulkStatus !== '' || bulkPriority !== '' || bulkAssignee !== '');

	/** Applies the bulk bar's change to every ticked ticket. */
	async function applyBulk(): Promise<void> {
		applyingBulk = true;
		/** One call for the lot; the list and counts are re-read after. */
		try {
			const updated = await bulkUpdateDeskTickets({
				ticketKeys: selected,
				...(bulkStatus ? { status: bulkStatus as SupportTicketStatus } : {}),
				...(bulkPriority ? { priority: bulkPriority as SupportTicketPriority } : {}),
				...(bulkAssignee ? { assigneeUUID: bulkAssignee === 'none' ? null : bulkAssignee } : {})
			});
			toastSuccess(t2('Updated {{count}} ticket(s).', { count: updated }));
			selected = [];
			bulkStatus = '';
			bulkPriority = '';
			bulkAssignee = '';
			await Promise.all([loadTickets(), loadStats()]);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to update the tickets'));
		} finally {
			applyingBulk = false;
		}
	}

	/**
	 * The SLA clock worth showing in the queue: first response until there has
	 * been one, resolution after.
	 */
	function nextClock(ticket: SupportTicketSummary): {
		label: string;
		dueAt: string | null | undefined;
		isBreached: boolean | undefined;
		isPaused: boolean | undefined;
	} {
		/* No reply yet: the first-response clock is the one running. */
		if (ticket.status === 'new') {
			return {
				label: t2('First response'),
				dueAt: ticket.firstResponseDueAt,
				isBreached: ticket.isFirstResponseBreached,
				isPaused: false
			};
		}
		return {
			label: t2('Resolution'),
			dueAt: ticket.resolutionDueAt,
			isBreached: ticket.isResolutionBreached,
			isPaused: ticket.isSLAPaused
		};
	}

	/** Status filter items. */
	let statusItems = $derived([
		{ value: OPEN, name: $t('Open (any)') },
		{ value: '', name: $t('Any status') },
		...SUPPORT_STATUSES.map((status) => ({ value: status, name: $t(supportStatusLabel(status)) }))
	]);

	/** Priority filter items. */
	let priorityItems = $derived([
		{ value: '', name: $t('Any priority') },
		...SUPPORT_PRIORITIES.map((priority) => ({ value: priority, name: $t(supportPriorityLabel(priority)) }))
	]);

	/** Product filter items. */
	let productItems = $derived([
		{ value: '', name: $t('Any product') },
		...products.map((product) => ({ value: product.applicationUUID, name: product.name }))
	]);

	/** Assignee filter items. */
	let assigneeItems = $derived([
		{ value: '', name: $t('Anyone') },
		...agents.map((agent) => ({ value: agent.userUUID, name: agent.name }))
	]);

	/** Bulk status items — never back to `new`. */
	let bulkStatusItems = $derived([
		{ value: '', name: $t('Status…') },
		...SUPPORT_STATUSES.filter((status) => status !== 'new').map((status) => ({
			value: status,
			name: $t(supportStatusLabel(status))
		}))
	]);

	/** Bulk priority items. */
	let bulkPriorityItems = $derived([
		{ value: '', name: $t('Priority…') },
		...SUPPORT_PRIORITIES.map((priority) => ({ value: priority, name: $t(supportPriorityLabel(priority)) }))
	]);

	/** Bulk assignee items. */
	let bulkAssigneeItems = $derived([
		{ value: '', name: $t('Assign to…') },
		{ value: 'none', name: $t('Nobody') },
		...agents.map((agent) => ({ value: agent.userUUID, name: agent.name }))
	]);

	onMount(() => {
		loadTickets();
		loadStats();
		loadFilterChoices();
	});
</script>

<PageHeader
	title={$t('Support Desk')}
	description={$t('Every ticket filed against a support-enabled product.')}
	breadcrumbs={[{ label: $t('Support Desk') }]}
/>

<!-- Filters -->
<Panel title={$t('Filters')} faIcon="filter" class="mb-6">
	<div class="flex flex-wrap items-center gap-3">
		<div class="max-w-xs flex-1">
			<Input
				bind:value={search}
				placeholder={$t('Search key, subject or requester…')}
				aria-label={$t('Search tickets…')}
				onkeydown={(event: KeyboardEvent) => event.key === 'Enter' && reload()}
			>
				{#snippet left()}<i class="fa-solid fa-search text-gray-400"></i>{/snippet}
			</Input>
		</div>
		<AbstractDropdown
			items={statusItems}
			bind:value={statusFilter}
			ariaLabel={$t('Status')}
			onchange={reload}
			class="w-44"
		/>
		<AbstractDropdown
			items={priorityItems}
			bind:value={priorityFilter}
			ariaLabel={$t('Priority')}
			onchange={reload}
			class="w-44"
		/>
		{#if products.length > 1}
			<AbstractDropdown
				items={productItems}
				bind:value={productFilter}
				ariaLabel={$t('Product')}
				onchange={reload}
				class="w-44"
			/>
		{/if}
		<!-- The assignee is already fixed by the unassigned and mine views. -->
		{#if view !== 'unassigned' && view !== 'mine'}
			<AbstractDropdown
				items={assigneeItems}
				bind:value={assigneeFilter}
				ariaLabel={$t('Assignee')}
				onchange={reload}
				class="w-44"
			/>
		{/if}
		{#if hasFilters}
			<Button size="sm" color="alternative" onclick={clearFilters}>
				<i class="fa-solid fa-xmark me-1"></i>{$t('Clear')}
			</Button>
		{/if}
		<div class="ms-auto flex gap-2">
			<RoutedButton color="alternative" href="/Support/Desk/Templates">
				<i class="fa-solid fa-file-lines me-2"></i>{$t('Templates')}
			</RoutedButton>
			<RoutedButton href="/Support/Desk/New">
				<i class="fa-solid fa-plus me-2"></i>{$t('New ticket')}
			</RoutedButton>
		</div>
	</div>
</Panel>

<div class="space-y-4">
	<div class="controlpanel flex flex-wrap gap-1 !p-1" role="tablist" aria-label={$t('Views')}>
		{#each VIEWS as item (item.key)}
			<button
				type="button"
				role="tab"
				aria-selected={view === item.key}
				data-view={item.key}
				onclick={() => selectView(item.key)}
				class="flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition {view === item.key
					? 'bg-primary-100 text-primary-600 dark:bg-primary-600/20 dark:text-primary-400'
					: 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}"
			>
				<i class="fa-solid {item.icon} text-xs"></i>{$t(item.label)}
				{#if stats}
					<span
						class="rounded-full px-1.5 text-xs {item.key === 'breaching' && stats[item.stat] > 0
							? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
							: 'opacity-70'}">{stats[item.stat]}</span
					>
				{/if}
			</button>
		{/each}
	</div>

	{#if selected.length > 0}
		<div
			class="bg-primary-50 dark:bg-primary-900/20 flex flex-wrap items-center gap-2 rounded-2xl px-3 py-2"
			role="region"
			aria-label={$t('Bulk actions')}
		>
			<span class="text-sm font-medium">{$t('{{count}} selected', { count: selected.length })}</span>
			<Select
				class="!w-40"
				size="sm"
				placeholder=""
				items={bulkStatusItems}
				value={bulkStatus}
				aria-label={$t('Set status')}
				onchange={(event: Event) => (bulkStatus = (event.currentTarget as HTMLSelectElement).value)}
			/>
			<Select
				class="!w-36"
				size="sm"
				placeholder=""
				items={bulkPriorityItems}
				value={bulkPriority}
				aria-label={$t('Set priority')}
				onchange={(event: Event) => (bulkPriority = (event.currentTarget as HTMLSelectElement).value)}
			/>
			<Select
				class="!w-40"
				size="sm"
				placeholder=""
				items={bulkAssigneeItems}
				value={bulkAssignee}
				aria-label={$t('Set assignee')}
				onchange={(event: Event) => (bulkAssignee = (event.currentTarget as HTMLSelectElement).value)}
			/>
			<Button size="sm" disabled={!hasBulkChange || applyingBulk} onclick={applyBulk}>
				{#if applyingBulk}<Spinner size="4" class="me-2" />{/if}{$t('Apply')}
			</Button>
			<Button size="sm" color="alternative" onclick={() => (selected = [])}>{$t('Clear selection')}</Button>
		</div>
	{/if}

	{#if loading}
		<EmptyState variant="loading" />
	{:else if error}
		<EmptyState variant="error" description={error}>
			<Button size="sm" onclick={loadTickets}>{$t('Retry')}</Button>
		</EmptyState>
	{:else if tickets.length === 0}
		<EmptyState
			faIcon="mug-hot"
			title={$t('Nothing in this view')}
			description={$t('No ticket matches this view and these filters.')}
		/>
	{:else}
		<Table divClass="widget !p-1 relative" class="dataTable">
			<TableHead>
				<TableHeadCell class="w-10 !px-2">
					<Checkbox checked={allSelected} onchange={toggleAll} aria-label={$t('Select all')} />
				</TableHeadCell>
				<SortableTableHeadCell key="ticketKey" class="w-28" {sortBy} {sortDir} onsort={handleSort}>
					{$t('Ticket')}
				</SortableTableHeadCell>
				<TableHeadCell>{$t('Subject')}</TableHeadCell>
				<TableHeadCell class="w-36">{$t('Status')}</TableHeadCell>
				<SortableTableHeadCell key="priority" class="w-28" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
					{$t('Priority')}
				</SortableTableHeadCell>
				<TableHeadCell class="w-36">{$t('Assignee')}</TableHeadCell>
				<SortableTableHeadCell key="resolutionDueAt" class="w-40" {sortBy} {sortDir} onsort={handleSort}>
					{$t('SLA')}
				</SortableTableHeadCell>
				<!-- Highest first on the first click; the server sorts unrated tickets last either way. -->
				<SortableTableHeadCell key="rating" class="w-24" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
					{$t('Rating')}
				</SortableTableHeadCell>
				<SortableTableHeadCell key="lastActivityAt" class="w-32" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
					{$t('Activity')}
				</SortableTableHeadCell>
			</TableHead>
			<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
				{#each tickets as ticket (ticket.ticketUUID)}
					{@const clock = nextClock(ticket)}
					<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
						<TableBodyCell class="!px-2">
							<Checkbox
								checked={selected.includes(ticket.ticketKey)}
								onchange={() => toggle(ticket.ticketKey)}
								aria-label={$t('Select {{key}}', { key: ticket.ticketKey })}
							/>
						</TableBodyCell>
						<TableBodyCell class="font-mono text-xs !whitespace-nowrap">
							<RoutedLink to="/Support/Desk/Tickets/{ticket.ticketKey}">{ticket.ticketKey}</RoutedLink>
						</TableBodyCell>
						<TableBodyCell>
							<RoutedLink to="/Support/Desk/Tickets/{ticket.ticketKey}">
								<span class="font-medium text-gray-900 dark:text-white">{ticket.subject}</span>
							</RoutedLink>
							<p class="text-default text-xs">
								{ticket.requester.name} · {ticket.application.name}{#if ticket.category}
									· {ticket.category.name}{/if}
							</p>
						</TableBodyCell>
						<TableBodyCell><SupportStatusBadge status={ticket.status} /></TableBodyCell>
						<TableBodyCell><SupportPriorityBadge priority={ticket.priority} /></TableBodyCell>
						<TableBodyCell class="text-sm">
							{#if ticket.assignee}
								{ticket.assignee.name}
							{:else}
								<Badge color="gray">{$t('Unassigned')}</Badge>
							{/if}
						</TableBodyCell>
						<TableBodyCell>
							{#if ticket.status === 'resolved' || ticket.status === 'closed'}
								<span class="text-xs text-gray-400">—</span>
							{:else}
								<p class="text-default text-xs">{clock.label}</p>
								<SupportSLATimer
									compact
									dueAt={clock.dueAt}
									isBreached={clock.isBreached}
									isPaused={clock.isPaused}
								/>
							{/if}
						</TableBodyCell>
						<TableBodyCell><SupportStars rating={ticket.satisfactionRating} /></TableBodyCell>
						<TableBodyCell class="text-default text-sm"><RelativeTime date={ticket.lastActivityAt} /></TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>

		{#if total > limit}
			<div class="flex items-center justify-between">
				<p class="text-default text-sm">
					{$t('Showing {{from}} to {{to}} of {{total}}', {
						from: page * limit + 1,
						to: Math.min((page + 1) * limit, total),
						total
					})}
				</p>
				<div class="flex gap-2">
					<Button
						size="sm"
						color="alternative"
						disabled={page === 0}
						onclick={() => {
							page--;
							loadTickets();
						}}><i class="fa-solid fa-chevron-left me-1"></i>{$t('Previous')}</Button
					>
					<Button
						size="sm"
						color="alternative"
						disabled={(page + 1) * limit >= total}
						onclick={() => {
							page++;
							loadTickets();
						}}>{$t('Next')}<i class="fa-solid fa-chevron-right ms-1"></i></Button
					>
				</div>
			</div>
		{/if}
	{/if}
</div>
